import { Col, Row } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { FormApi, FormState } from 'final-form';
import { Component, ComponentType, ReactNode } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import { Transition } from 'react-transition-group';

import { ContentLocalesI } from '~src/interfaces/session.types';
import { KeyTextObj } from '~src/interfaces/translation.interface';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { getContentLocale } from '~src/services/session';
import CustomTextArea from '~src/shared/form/inputs/CustomTextArea';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import SelectInput from '~src/shared/form/select/SelectInput';
import * as SC from '~src/shared/form/translatableFormShared.sc';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import arrayMutators from '~src/shared/reactFinalForm/arrayMutators';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { FieldType, PartialBy } from '~src/types';
import { mapKeyToDataValue, trimValues, unfoldErrorResponse } from '~src/utils';

import CheckBox, { CheckBoxType } from '~src/shared/form/Checkbox';
import { SectionOpt, TranslatableField } from './interfaces';
import { FieldWrapper, SectionTitle } from './translatableSectionForm.sc';
import {TFunction} from "i18next";
import {objectDiff} from "~src/utils/objectDiff";

const duration = 200;

export type FormValuesI = Record<string, string | Record<string, string>>;
interface DefaultProps {
  submitBtnKey: string;
  cancelBtnKey: string;
  inContainer: boolean;
  hideButtons: boolean;
  submitOnlyChanged: boolean;
}

interface OwnProps extends DefaultProps {
  t: TFunction;
  form: string;
  sections: SectionOpt[];
  titleKey?: string;
  title?: string;
  tooltip?: KeyTextObj;
  loadingSave: boolean;
  onCancel: () => void;
  onChange?: (values: FormValuesI) => void;
  onSubmit: (values: FormValuesI) => ApiReduxResp;
  onToggleTranslationBox?: (show: boolean) => void;
  additionalSection?: ReactNode;
}

export type TSFormData = {
  selectedContentLocale: string;
} & FormValuesI;

interface StateProps {
  defaultContentLocale: string;
  contentLocales: ContentLocalesI;
}

type Props = OwnProps & StateProps;

interface State {
  showTranslationBox: boolean;
  selectedContentLocale: string;
}

class TranslatableSectionForm extends Component<Props, State> {
  static defaultProps: DefaultProps = {
    submitBtnKey: 'x.shared.form.save_button',
    cancelBtnKey: 'x.shared.form.cancel_button',
    inContainer: false,
    hideButtons: false,
    submitOnlyChanged: false,
  };

  state = {
    showTranslationBox: false,
    selectedContentLocale: this.props.defaultContentLocale,
  };

  onDropDownSelect = (value: SelectValue) => {
    this.setState({ selectedContentLocale: value as string });
  };

  toggleTranslationBox = () => {
    this.setState(
      (prevState) => ({ showTranslationBox: !prevState.showTranslationBox }),
      () => {
        if (this.props.onToggleTranslationBox) {
          this.props.onToggleTranslationBox(this.state.showTranslationBox);
        }
      },
    );
  };

  doSubmit = (values: TSFormData) => {
    let adjustedValues: Omit<TSFormData, 'selectedContentLocale'> & { selectedContentLocale?: string } =
      trimValues(values);
    delete adjustedValues.selectedContentLocale;
    const initialValues = this.getInitialValues(this.props.sections, this.props.defaultContentLocale);
    if (this.props.submitOnlyChanged) {
      adjustedValues = Object.keys(adjustedValues).reduce((acc: Record<string, any>, key) => {
        if (initialValues[key]) {
          acc[key] = objectDiff(initialValues[key], adjustedValues[key]);
        } else if (key === 'users' && Number(adjustedValues[key].length) > 0) {
          acc[key] = (adjustedValues[key] as any).map((person: any) => person.id); // wtf is that
        }
        return acc;
      }, {});
    }
    return this.props.onSubmit(adjustedValues).then((resp) => {
      const { error } = resp;
      if (error && error.response && error.response.data) {
        return unfoldErrorResponse(error.response.data);
      }
    });
  };

  renderTextInput = ({ autoFocus, input, label, labelKey, meta: { error, touched } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        type="text"
        error={touched && error ? error : null}
        label={label}
        labelKey={labelKey}
      />
    );
  };

  renderCheckbox = ({ input, label, labelKey }: FieldType) => {
    return (
      <CheckBox
        {...input}
        isChecked={input.checked}
        type={CheckBoxType.CheckBox}
        label={label}
        id={labelKey}
        labelKey={labelKey}
      />
    );
  };

  renderTextArea = ({ autoFocus, input, label, labelKey, meta: { error, touched } }: FieldType) => (
    <FieldWrapper>
      <CustomTextArea
        {...input}
        autoFocus={autoFocus}
        type="text"
        error={touched && error ? error : null}
        label={label}
        labelKey={labelKey}
      />
    </FieldWrapper>
  );

  renderSelectInput = ({
    input,
    label,
    labelKey,
    list,
    meta: { touched, error },
  }: FieldType & {
    list: Record<string, string>;
  }) => {
    const optList = Object.keys(list).map((k) => ({ name: list[k], value: k }));
    return (
      <SelectInput
        {...input}
        t={this.props.t}
        label={label}
        labelKey={labelKey}
        optList={optList}
        error={touched && error ? error : null}
        onSelect={this.onDropDownSelect}
      />
    );
  };

  renderField = (field: TranslatableField, locale: string, focus = false) => {
    const isTranslation = locale !== this.props.defaultContentLocale;
    let renderFunc = this.renderTextInput;
    let fieldName = `${field.name}.${locale}`;

    if (field.type === 'textArea') {
      renderFunc = this.renderTextArea;
    }

    if (field.type === 'checkbox') {
      renderFunc = this.renderCheckbox;
      fieldName = field.name;
    }

    return (
      <Field
        key={field.name}
        name={fieldName}
        label={this.props.t(field.label)}
        focus={focus}
        children={renderFunc}
        type={field.type}
        labelKey={field.label}
        validate={field[isTranslation ? 'translationValidator' : 'validator'] || undefined}
      />
    );
  };

  renderSection = (section: SectionOpt, defaultContentLocale: string, SecIndex: number, noLabel = false) => {
    const { t } = this.props;
    return (
      <div key={section.name}>
        {section.titleKey && (
          <SectionTitle>
            {!noLabel && (
              <>
                <span>{t(section.titleKey)}</span>
                {section.descriptionKey && (
                  <HoverInfoIcon
                    infoText={{
                      key: section.descriptionKey,
                      txt: t(section.descriptionKey),
                    }}
                  />
                )}
              </>
            )}
          </SectionTitle>
        )}
        {section.fields.map((field: TranslatableField, index) =>
          this.renderField(field, defaultContentLocale, SecIndex === 0 && index === 0),
        )}
      </div>
    );
  };

  createCancel = (form: FormApi<any>) => {
    return () => {
      form.reset();
      this.props.onCancel();
    };
  };

  onChange = (state: FormState<FormValuesI>) => this.props.onChange && this.props.onChange(state.values);

  render() {
    const { t, titleKey, sections, contentLocales, defaultContentLocale } = this.props;
    const initialValues = this.getInitialValues(sections, defaultContentLocale);
    const translatableForm = (
      <Form
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        keepDirtyOnReinitialize
        onSubmit={this.doSubmit}
      >
        {({ dirty, form, handleSubmit, submitting }) => {
          return (
            <form onSubmit={handleSubmit} noValidate id={this.props.form}>
              <FormSpy subscription={{ values: true }} onChange={this.onChange} />
              {titleKey && (
                <TileHeader
                  title={this.props.title || t(titleKey)}
                  titleKey={titleKey}
                  tooltip={this.props.tooltip}
                  border
                />
              )}
              <SC.FormContent $inContainer={this.props.inContainer}>
                <Transition in={this.state.showTranslationBox} timeout={duration}>
                  {(status) => (
                    <SC.FormContainer>
                      <SC.DefaultContainer $duration={duration} $toggled={this.state.showTranslationBox}>
                        <SC.LeftColHeader>
                          <div>
                            <SC.DefLangTitle data-translation="x.shared.name_and_description_form.default_language">
                              {t('x.shared.name_and_description_form.default_language')}
                            </SC.DefLangTitle>
                            <SC.DefLangTxt>{this.props.contentLocales[defaultContentLocale]}</SC.DefLangTxt>
                          </div>
                          <SC.ToggleTranslationBtn onClick={this.toggleTranslationBox}>
                            {!this.state.showTranslationBox && (
                              <span data-translation="x.shared.name_and_description_form.edit_translations">
                                {t('x.shared.name_and_description_form.edit_translations')}
                              </span>
                            )}
                          </SC.ToggleTranslationBtn>
                        </SC.LeftColHeader>
                        <SC.Fieldset>
                          {sections.map((section: SectionOpt, index: number) =>
                            this.renderSection(section, defaultContentLocale, index),
                          )}
                        </SC.Fieldset>
                      </SC.DefaultContainer>
                      <SC.TranslationContainer
                        $duration={duration}
                        $status={status}
                        $toggled={this.state.showTranslationBox}
                      >
                        {(status === 'entering' || status === 'exiting' || this.state.showTranslationBox) && (
                          <>
                            <SC.LeftColHeader>
                              <SC.ContentLocaleSelectBox>
                                <Field
                                  name="selectedContentLocale"
                                  label={t('x.users.content_language')}
                                  labelKey="x.users.content_language"
                                  children={this.renderSelectInput}
                                  list={contentLocales}
                                />
                              </SC.ContentLocaleSelectBox>
                              <SC.ToggleTranslationBtn onClick={this.toggleTranslationBox}>
                                {this.state.showTranslationBox && (
                                  <span data-translation="x.shared.name_and_description_form.hide_translations">
                                    {t('x.shared.name_and_description_form.hide_translations')}
                                  </span>
                                )}
                              </SC.ToggleTranslationBtn>
                            </SC.LeftColHeader>
                            {Object.keys(contentLocales).map((locale) => {
                              if (this.state.selectedContentLocale === locale) {
                                return (
                                  <SC.Fieldset key={locale}>
                                    {sections.map((section: SectionOpt, index: number) => (
                                      <div key={section.name}>
                                        {this.renderSection(section, locale, index, true)}
                                      </div>
                                    ))}
                                  </SC.Fieldset>
                                );
                              }
                              return null;
                            })}
                          </>
                        )}
                      </SC.TranslationContainer>
                    </SC.FormContainer>
                  )}
                </Transition>
                <Row gutter={30}>{this.props.children}</Row>
                <Row gutter={30}>
                  <Col span={24}>
                    <SC.ButtonsPanel>
                      {(dirty || !this.props.hideButtons) && (
                        <>
                          <SC.CancelBtn
                            type="button"
                            onClick={this.createCancel(form)}
                            data-translation={this.props.cancelBtnKey}
                            disabled={submitting}
                          >
                            {t(this.props.cancelBtnKey)}
                          </SC.CancelBtn>
                          <SC.SubmitBtn isLoading={this.props.loadingSave}>
                            <span data-translation={this.props.submitBtnKey}>
                              {t(this.props.submitBtnKey)}
                            </span>
                          </SC.SubmitBtn>
                        </>
                      )}
                    </SC.ButtonsPanel>
                  </Col>
                </Row>
              </SC.FormContent>
            </form>
          );
        }}
      </Form>
    );
    return this.props.inContainer ? (
      <SC.TileContainerS className={!titleKey ? 'no-title' : ''}>{translatableForm}</SC.TileContainerS>
    ) : (
      translatableForm
    );
  }

  getInitialValues = (sections: SectionOpt[], defaultContentLocale: string): TSFormData => {
    const allSections = sections.reduce((acc: TranslatableField[], section) => {
      acc = [...acc, ...section.fields];
      return acc;
    }, []);
    const initFrmVals = mapKeyToDataValue(allSections, 'name', 'value');
    return {
      ...initFrmVals,
      selectedContentLocale: defaultContentLocale,
    };
  };
}

const mapStateToProps = (state: StoreI): StateProps => {
  const defaultContentLocale = getContentLocale(state);
  const { contentLocales } = state.user.session;

  return {
    defaultContentLocale,
    contentLocales,
  };
};

export default connect(mapStateToProps)(TranslatableSectionForm) as ComponentType<
  PartialBy<OwnProps, keyof DefaultProps>
>;
